import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { extractLinkedInUrn } from 'utils/extractLinkedInUrn'

import styles from './EmbeddedLIPost.pcss'

const DEFAULT_POST_WIDTH = 504
const DEFAULT_POST_HEIGHT = 600

export interface EmbeddedLIPostProps {
  url: string
  width?: number
  height?: number
  onLoaded?: () => void
}

export function EmbeddedLIPost(props: EmbeddedLIPostProps) {
  const urn = extractLinkedInUrn(props.url)
  const embedUrl = urn ? `https://www.linkedin.com/embed/feed/update/${urn}` : ''
  const [blocked, setBlocked] = useState(false)
  // EXPL: callers may pass a non-memoized callback that should not re-trigger the probe
  const onLoadedRef = useRef(props.onLoaded)
  useEffect(() => {
    onLoadedRef.current = props.onLoaded
  })

  // EXPL: content blockers (Brave shields, uBlock) drop linkedin's embed endpoint at the network
  // layer. The iframe still fires `load` for the browser's own error page, so it cannot tell us
  // anything - probe the same url instead, where a blocked request rejects outright.
  useEffect(() => {
    if (!embedUrl) {
      return
    }

    let cancelled = false

    fetch(embedUrl, { mode: 'no-cors' }).catch(() => {
      if (cancelled) {
        return
      }
      setBlocked(true)
      onLoadedRef.current?.()
    })

    return () => {
      cancelled = true
    }
  }, [embedUrl])

  const originalLink = props.url
    ? (
      <a href={props.url} target="_blank" rel="noopener" className={styles.link}>
        <FormattedMessage id="content.view-on-linkedin" />
      </a>
    )
    : null

  if (!urn) {
    console.error('[EmbeddedLIPost] could not extract a post urn from url', props.url)
    return (
      <div className={styles.container}>
        <p className={styles.message}><FormattedMessage id="errors.generic" /></p>
        {originalLink}
      </div>
    )
  }

  if (blocked) {
    return (
      <div className={styles.container}>
        <p className={styles.message}><FormattedMessage id="errors.linkedin-embed-blocked" /></p>
        {originalLink}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <iframe
        src={embedUrl}
        width={props.width || DEFAULT_POST_WIDTH}
        height={props.height || DEFAULT_POST_HEIGHT}
        frameBorder="0"
        allowFullScreen
        title="Embedded LinkedIn post"
        data-testid="embedded-li-post"
        className={styles.frame}
        onLoad={props.onLoaded}
      />
      {originalLink}
    </div>
  )
}

export default EmbeddedLIPost
